import { useState } from 'react';
import './NotesMode.css';

function formatDateTime(dateString) {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
}

export default function NotesMode({ notes, onAddNote, saving, users }) {
  const [showForm, setShowForm] = useState(false);
  const [noteType, setNoteType] = useState('CALL');
  const [rawText, setRawText] = useState('');
  const [expandedNoteId, setExpandedNoteId] = useState(null);

  function handleAddClick() {
    setShowForm(true);
  }

  function handleCancel() {
    setShowForm(false);
    setRawText('');
    setNoteType('CALL');
  }

  async function handleSave() {
    if (!rawText.trim()) return;
    await onAddNote(noteType, rawText.trim());
    setShowForm(false);
    setRawText('');
    setNoteType('CALL');
  }

  function handleToggleExpand(noteId) {
    if (expandedNoteId === noteId) {
      setExpandedNoteId(null);
    } else {
      setExpandedNoteId(noteId);
    }
  }

  function getAuthorName(authorUserId) {
    if (!users || !authorUserId) return '—';
    const user = users.find(u => u._id === authorUserId);
    return user ? user.name : authorUserId;
  }

  function getNoteTypeLabel(type) {
    if (type === 'CALL') return '📞 Call';
    if (type === 'MEETING') return '🤝 Meeting';
    return '📝 General';
  }

  function getNoteTypeClass(type) {
    if (type === 'CALL') return 'cj-notes__type cj-notes__type--call';
    if (type === 'MEETING') return 'cj-notes__type cj-notes__type--meeting';
    return 'cj-notes__type cj-notes__type--general';
  }

  return (
    <div className="cj-notes">
      <div className="cj-notes__toolbar">
        {!showForm && (
          <button className="cj-notes__add-btn" onClick={handleAddClick}>
            + New Note
          </button>
        )}
      </div>

      {showForm && (
        <div className="cj-notes__form">
          <div className="cj-notes__form-type-row">
            {['CALL', 'MEETING', 'GENERAL'].map(type => (
              <button
                key={type}
                className={`cj-notes__type-btn ${noteType === type ? 'cj-notes__type-btn--active' : ''}`}
                onClick={() => setNoteType(type)}
              >
                {getNoteTypeLabel(type)}
              </button>
            ))}
          </div>
          <textarea
            className="cj-notes__textarea"
            placeholder="Write your notes here..."
            value={rawText}
            onChange={e => setRawText(e.target.value)}
            rows={5}
            autoFocus
          />
          <div className="cj-notes__form-actions">
            <button
              className="cj-notes__save-btn"
              onClick={handleSave}
              disabled={saving || !rawText.trim()}
            >
              {saving ? 'Saving...' : 'Save Note'}
            </button>
            <button className="cj-notes__cancel-btn" onClick={handleCancel} disabled={saving}>
              Cancel
            </button>
          </div>
          <p className="cj-notes__ai-hint">
            AI summary will be generated automatically in a future release.
          </p>
        </div>
      )}

      {!notes || notes.length === 0 ? (
        <div className="cj-notes__empty">
          <p>No notes yet.</p>
          <p className="cj-notes__empty-sub">Add a call or meeting note to track interactions with this client.</p>
        </div>
      ) : (
        <div className="cj-notes__list">
          {notes.map(note => (
            <div key={note.id} className="cj-notes__card">
              <div className="cj-notes__card-header">
                <div className="cj-notes__card-meta">
                  <span className={getNoteTypeClass(note.noteType)}>{getNoteTypeLabel(note.noteType)}</span>
                  <span className="cj-notes__card-date">{formatDateTime(note.timestamp)}</span>
                  <span className="cj-notes__card-author">{getAuthorName(note.authorUserId)}</span>
                </div>
                <button
                  className="cj-notes__card-expand"
                  onClick={() => handleToggleExpand(note.id)}
                >
                  {expandedNoteId === note.id ? 'Hide ▴' : 'View ▾'}
                </button>
              </div>

              {note.aiSummary ? (
                <p className="cj-notes__card-summary">{note.aiSummary}</p>
              ) : (
                <p className="cj-notes__card-preview">
                  {note.rawText && note.rawText.length > 120
                    ? note.rawText.slice(0, 120) + '...'
                    : note.rawText}
                </p>
              )}

              {expandedNoteId === note.id && (
                <div className="cj-notes__card-full">
                  <div className="cj-notes__card-divider" />
                  <p className="cj-notes__card-raw">{note.rawText}</p>
                  {note.actionItems && note.actionItems.length > 0 && (
                    <div className="cj-notes__action-items">
                      <p className="cj-notes__action-label">Action items:</p>
                      <ul>
                        {note.actionItems.map((item, i) => (
                          <li key={i}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
